import { memo, useMemo, useReducer, type FunctionComponent } from 'react';
import size from 'lodash/size';
import filter from 'lodash/filter';
import toSafeInteger from 'lodash/toSafeInteger';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
// Skillmore UI Components
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import GridBox from '@empathco/ui-components/src/mixins/GridBox';
import MatchIndicator, { type MatchIndicatorVariant } from '@empathco/ui-components/src/elements/MatchIndicator';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { AdminEmployee, AdminJob } from '../graphql/types';
import { TalentEmployeeObject, DevPlanEmployee, TalentEmployeeDetails } from '../graphql/customTypes';
import { Job } from '../models/job';
import { Employee } from '../models/employee';
import { toggleReducer } from '../helpers/reducers';
import EmployeeSimpleBand from '../elements/EmployeeSimpleBand';
import EmployeeDetailsPopup from '../v3/EmployeeDetailsPopup';
// SCSS imports
import { label, alone } from './EmployeeTalentBand.module.scss';

type TalentBandVariant = 'standalone';

type EmployeeTalentBandProps = {
  readonly employee?: TalentEmployeeObject | DevPlanEmployee | TalentEmployeeDetails | AdminEmployee;
  readonly variant?: TalentBandVariant;
  readonly matchIndicatorVariant?: MatchIndicatorVariant;
  readonly route?: string | null;
  readonly onJobClick?: (code: string, job?: Job | AdminJob, context?: TalentEmployeeObject | AdminEmployee) => void;
  readonly disabled?: boolean | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly isMyPlan?: boolean;
  readonly withReloading?: boolean;
  readonly withoutDivider?: boolean;
}

const EmployeeTalentBand: FunctionComponent<EmployeeTalentBandProps> = ({
  employee,
  variant,
  matchIndicatorVariant,
  route,
  onJobClick,
  disabled = false,
  pending = false,
  failed = false,
  isMyPlan = false,
  withReloading = false,
  withoutDivider = false
  // eslint-disable-next-line complexity
}) => {
  const { match_rate } = employee as Employee || {};
  const { current_match_rate, skills } = employee as DevPlanEmployee || {};
  const isStandalone = variant === 'standalone';
  const withMatchIndicator = Boolean(matchIndicatorVariant);

  const [popupOpen, togglePopupOpen] = useReducer(toggleReducer, false);

  const matchValues = useMemo(() => withMatchIndicator ? {
    satisfied: size(filter(skills, 'is_satisfied')),
    total: size(skills)
  } : undefined, [skills, withMatchIndicator]);

  const reloading = Boolean(withReloading && pending && employee);

  return (failed && <FetchFailedAlert flat={isStandalone}/>) || (
    pending && !reloading && <LoadingPlaceholder flat={isStandalone}/>
  ) || (
    <>
      <ReloadingOverlay reloading={reloading}>
        {failed || (pending && !reloading) ? undefined : (
          <GridBox
              container
              spacing={2}
              pt={isStandalone ? 2 : 3}
              pb={isStandalone ? (withMatchIndicator && 1) || 4 : 2}
              px={isStandalone ? (withMatchIndicator && 3) || 6 : undefined}
              className={isStandalone ? alone : undefined}
          >
            {isMyPlan || !employee ? (
              <GridBox
                  container
                  size={{
                    xs: withMatchIndicator ? 8 : undefined,
                    sm: withMatchIndicator ? 9 : undefined,
                    md: withMatchIndicator ? 10 : undefined
                  }}
                  alignItems="center"
              >
                <BoxTypography pl={2.5} variant="h3">
                  <FormattedMessage id="dev_plans.match_rate"/>
                </BoxTypography>
              </GridBox>
            ) : (
              <EmployeeSimpleBand
                  employee={employee}
                  route={route}
                  onJobClick={onJobClick}
                  disabled={disabled}
                  isStandalone={isStandalone}
                  withMatchIndicator={withMatchIndicator}
              />
            )}
            {withMatchIndicator ? (
              <GridBox
                  size={{ xs: 4, sm: 3, md: 2 }}
                  container
                  flexDirection="column"
                  alignItems="center"
              >
                <BoxTypography variant="caption" color="text.label" pt={1.5} className={label}>
                  <FormattedMessage id="hr.talentfinder.column.match_skills" values={matchValues}/>
                </BoxTypography>
                <IconButton
                    size="small"
                    disabled={disabled ? true : undefined}
                    onClick={togglePopupOpen}
                >
                  <MatchIndicator
                      value={toSafeInteger(match_rate || current_match_rate)}
                      variant={matchIndicatorVariant}
                  />
                </IconButton>
              </GridBox>
            ) : undefined}
          </GridBox>
        )}
      </ReloadingOverlay>
      {withoutDivider ? undefined : <Divider/>}
      {withMatchIndicator ? (
        <EmployeeDetailsPopup
            isMyPlan={isMyPlan}
            employee={employee as TalentEmployeeObject}
            isOpen={popupOpen}
            onClose={togglePopupOpen}
            route={route}
        />
      ) : undefined}
    </>
  );
};

export default memo(EmployeeTalentBand);
